import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
interface ReceiptSummaryProps {
  totalThisReceipt: number;
  totalReceived: number;
  totalRemaining: number;
  poTotal: number;
  currency: string;
  locale?: string;
}
export function ReceiptSummary({
  totalThisReceipt,
  totalReceived,
  totalRemaining,
  poTotal,
  currency,
  locale = 'en-US'
}: ReceiptSummaryProps) {
  // Calculate percentages for progress bars
  const receivedPercentage = totalReceived / poTotal * 100;
  const thisReceiptPercentage = totalThisReceipt / poTotal * 100;
  const remainingPercentage = totalRemaining / poTotal * 100;
  // Calculate combined percentage (received + this receipt)
  const combinedPercentage = receivedPercentage + thisReceiptPercentage;
  // Check if we're at 100% after this receipt
  const isComplete = Math.abs(combinedPercentage - 100) < 0.01;
  return <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-12477">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-12478">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-12479">
          Receipt Summary
        </h3>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-12480">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-12481">
          <div className="p-4 bg-green-50 rounded-lg" data-id="element-12482">
            <div className="flex justify-between items-center" data-id="element-12483">
              <h4 className="text-sm font-medium text-green-800" data-id="element-12484">
                Total This Receipt
              </h4>
              <ArrowUpIcon className="h-4 w-4 text-green-600" data-id="element-12485" />
            </div>
            <p className="text-2xl font-mono font-semibold text-green-700" data-id="element-12486">
              {formatCurrency(totalThisReceipt, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-12487">
              <div className="text-xs text-green-700 mb-1" data-id="element-12488">
                {thisReceiptPercentage.toFixed(1)}% of PO Total
              </div>
              <div className="w-full bg-green-200 rounded-full h-2" data-id="element-12489">
                <div className="bg-green-600 h-2 rounded-full" style={{
                width: `${thisReceiptPercentage}%`
              }} data-id="element-12490"></div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg" data-id="element-12491">
            <div className="flex justify-between items-center" data-id="element-12492">
              <h4 className="text-sm font-medium text-blue-800" data-id="element-12493">
                Total Received To Date
              </h4>
              <CheckCircleIcon className="h-4 w-4 text-blue-600" data-id="element-12494" />
            </div>
            <p className="text-2xl font-mono font-semibold text-blue-700" data-id="element-12495">
              {formatCurrency(totalReceived, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-12496">
              <div className="text-xs text-blue-700 mb-1" data-id="element-12497">
                {receivedPercentage.toFixed(1)}% of PO Total
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2" data-id="element-12498">
                <div className="bg-blue-600 h-2 rounded-full" style={{
                width: `${receivedPercentage}%`
              }} data-id="element-12499"></div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-amber-50 rounded-lg" data-id="element-12500">
            <div className="flex justify-between items-center" data-id="element-12501">
              <h4 className="text-sm font-medium text-amber-800" data-id="element-12502">
                Remaining After Receipt
              </h4>
              {isComplete ? <CheckCircleIcon className="h-4 w-4 text-green-600" data-id="element-12503" /> : <AlertTriangleIcon className="h-4 w-4 text-amber-600" data-id="element-12504" />}
            </div>
            <p className="text-2xl font-mono font-semibold text-amber-700" data-id="element-12505">
              {formatCurrency(totalRemaining - totalThisReceipt, currency, locale)}
            </p>
            <div className="mt-2" data-id="element-12506">
              <div className="text-xs text-amber-700 mb-1" data-id="element-12507">
                {(remainingPercentage - thisReceiptPercentage).toFixed(1)}% of
                PO Total
              </div>
              <div className="w-full bg-amber-200 rounded-full h-2" data-id="element-12508">
                <div className="bg-amber-600 h-2 rounded-full" style={{
                width: `${remainingPercentage - thisReceiptPercentage}%`
              }} data-id="element-12509"></div>
              </div>
            </div>
          </div>
        </div>
        {/* Combined progress bar */}
        <div className="mt-6" data-id="element-12510">
          <div className="flex justify-between items-center mb-2" data-id="element-12511">
            <h4 className="text-sm font-medium text-gray-700" data-id="element-12512">
              Overall PO Receipt Progress
            </h4>
            <div className="text-sm text-gray-500" data-id="element-12513">
              PO Total: {formatCurrency(poTotal, currency, locale)}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden" data-id="element-12514">
            <div className="flex h-full" data-id="element-12515">
              <div className="bg-blue-600 h-4" style={{
              width: `${receivedPercentage}%`
            }} data-id="element-12516"></div>
              <div className="bg-green-500 h-4" style={{
              width: `${thisReceiptPercentage}%`
            }} data-id="element-12517"></div>
            </div>
          </div>
          <div className="flex mt-2 text-xs" data-id="element-12518">
            <div className="flex items-center mr-4" data-id="element-12519">
              <div className="w-3 h-3 bg-blue-600 rounded-full mr-1" data-id="element-12520"></div>
              <span data-id="element-12521">Already Received ({receivedPercentage.toFixed(1)}%)</span>
            </div>
            <div className="flex items-center mr-4" data-id="element-12522">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-1" data-id="element-12523"></div>
              <span data-id="element-12524">This Receipt ({thisReceiptPercentage.toFixed(1)}%)</span>
            </div>
            <div className="flex items-center" data-id="element-12525">
              <div className="w-3 h-3 bg-gray-300 rounded-full mr-1" data-id="element-12526"></div>
              <span data-id="element-12527">Remaining ({(100 - combinedPercentage).toFixed(1)}%)</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}